import AddIcon from "@mui/icons-material/Add";
import CleaningServicesOutlined from "@mui/icons-material/CleaningServicesOutlined";
import FilterAltOffOutlinedIcon from "@mui/icons-material/FilterAltOffOutlined";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import {
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
} from "@mui/material";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { BackdropCustom } from "../../components/backdrop/Backdrop";
import { PageCard } from "../../components/pageCard/PageCard";
import { CustomMenuItem } from "../../components/table/CustomMenuItem/CustomMenuItem";
import { CustomNoContentTableBody } from "../../components/table/CustomNoContentTableBody/CustomNoContentTableBody";
import { CustomTableCell } from "../../components/table/CustomTableCell/CustomTableCell";
import CustomTablePagination from "../../components/table/CustomTablePagination/CustomTablePagination";
import CustomTableSortLabel from "../../components/table/CustomTableSortLabel/CustomTableSortLabel";
import { FilterButtonGroup } from "../../components/table/FilterButtonGroup/FilterButtonGroup";
import { GridFilterArea } from "../../components/table/GridFilterArea/GridFilterArea";
import { TitleContainer } from "../../components/titleContainer/TitleContainer";
import { useToastr } from "../../components/toast/Toast";
import { useAuth } from "../../hooks/auth";
import { IListAllParams } from "../../interfaces/IListAllParams";
import { IPageProps } from "../../interfaces/IPageProps";
import { IUser } from "../../interfaces/IUser";
import api from "../../services/api";

const fields = [
  {
    field: "name",
    label: "Nome",
  },
  {
    field: "username",
    label: "Usuário",
  },
  {
    field: "email",
    label: "E-mail",
  },
  {
    field: "typeDescription",
    label: "Tipo",
  },
];

const STORAGE_KEY = "@ORQUIDEAS:usersFilters";

const getInitialState = (key: string, defaultValue: any) => {
  const saved = localStorage.getItem(STORAGE_KEY);
  if (saved) {
    const parsed = JSON.parse(saved);
    return parsed[key] !== undefined ? parsed[key] : defaultValue;
  }
  return defaultValue;
};

export const Users: React.FC<IPageProps> = ({ pageTitle }) => {
  window.document.title = pageTitle;

  const toastr = useToastr();
  const timeout = useRef<any>(null);
  const { signOut } = useAuth();
  const navigate = useNavigate();

  const [users, setUsers] = useState<IUser[]>([]);

  const [totalRows, setTotalRows] = useState(0);
  const [perPage, setPerPage] = useState<number>(() =>
    getInitialState("perPage", 10),
  );
  const [currentPage, setCurrentPage] = useState<number>(() =>
    getInitialState("currentPage", 0),
  );
  const [orderField, setOrderField] = useState<string>(() =>
    getInitialState("orderField", "created_at"),
  );
  const [orderDirection, setOrderDirection] = useState<"asc" | "desc">(() =>
    getInitialState("orderDirection", "desc"),
  );
  const [loading, setLoading] = useState(false);

  const [gridFilterAreaOpen, setGridFilterAreaOpen] = useState(() =>
    getInitialState("gridFilterAreaOpen", false),
  );
  const [filterField, setFilterField] = useState<string>(() =>
    getInitialState("filterField", ""),
  );
  const [filterPrecision, setFilterPrecision] = useState<string>(() =>
    getInitialState("filterPrecision", "containing"),
  );
  const [filterValue, setFilterValue] = useState<string>(() =>
    getInitialState("filterValue", ""),
  );
  const [delay, setDelay] = useState(0);

  const handleToUser = useCallback(
    (id: string | null) => {
      !!id ? navigate(`/user/${id}`) : navigate("/user");
    },
    [navigate],
  );

  const handleListAll = useCallback(
    async ({
      perPage,
      currentPage,
      orderField,
      orderDirection,
      field,
      value,
      precision,
      delay,
    }: IListAllParams) => {
      let url = `users?perPage=${perPage}&currentPage=${
        currentPage + 1
      }&orderBy=${orderField}&orderDirection=${orderDirection}`;

      if (field && value && precision) {
        url =
          url +
          `&filterField=${field}&filterValue=${value}&precision=${precision}`;
      }

      clearInterval(timeout.current);
      timeout.current = setTimeout(async () => {
        setLoading(true);
        await api
          .get(url)
          .then(respose => {
            const responseData = respose.data;

            if (responseData.data?.length > 0) {
              setUsers(responseData.data);
              setTotalRows(responseData.totalRows);
              setLoading(false);
            } else {
              setUsers([]);
              setTotalRows(0);
              setPerPage(10);
              setCurrentPage(0);
              setLoading(false);
            }
          })
          .catch(error => {
            if (error.response.status === 401) {
              signOut();
              navigate("/");
            }

            toastr.error(
              error?.response?.data?.message || "Erro ao listar usuários",
            );
          })
          .finally(() => {
            setLoading(false);
          });
      }, delay || 0);
    },
    [
      timeout,
      setLoading,
      setUsers,
      setTotalRows,
      setPerPage,
      setCurrentPage,
      signOut,
      navigate,
      toastr,
    ],
  );

  const handlePagination = async (
    perPage: number,
    currentPage: number,
    orderField: string,
    orderDirection: "asc" | "desc",
  ) => {
    handleListAll({
      perPage,
      currentPage,
      orderField,
      orderDirection,
      field: filterField,
      value: filterValue,
      precision: filterPrecision,
      delay: 0,
    });
  };

  useEffect(() => {
    const field = filterField;
    const value = filterValue;
    const precision = filterPrecision;

    handleListAll({
      perPage,
      currentPage,
      orderField,
      orderDirection,
      field,
      value,
      precision,
      delay,
    });
  }, [
    filterField,
    filterPrecision,
    filterValue,
    delay,
    currentPage,
    orderDirection,
    orderField,
    perPage,
    handleListAll,
  ]);

  const handleTextFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFilterValue(e.target.value);
    setTotalRows(0);
    setCurrentPage(0);
    setDelay(500);
  };

  const saveFiltersToStorage = useCallback(() => {
    const filters = {
      filterField,
      filterPrecision,
      filterValue,
      gridFilterAreaOpen,
      currentPage,
      perPage,
      orderField,
      orderDirection,
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(filters));
  }, [
    filterField,
    filterPrecision,
    filterValue,
    gridFilterAreaOpen,
    currentPage,
    perPage,
    orderField,
    orderDirection,
  ]);

  useEffect(() => {
    saveFiltersToStorage();
  }, [
    filterField,
    filterPrecision,
    filterValue,
    gridFilterAreaOpen,
    currentPage,
    perPage,
    saveFiltersToStorage,
  ]);

  const handleClearFilters = () => {
    setFilterField("");
    setFilterPrecision("containing");
    setFilterValue("");
    setCurrentPage(0);
    setPerPage(10);
    setOrderField("created_at");
    setOrderDirection("desc");
    localStorage.removeItem(STORAGE_KEY);
  };

  return (
    <>
      <TitleContainer>Usuários</TitleContainer>
      <PageCard>
        <FilterButtonGroup>
          <div>
            <Tooltip title="Filtros" placement="top">
              <IconButton
                onClick={() => setGridFilterAreaOpen(!gridFilterAreaOpen)}
                size="small"
                color="primary"
                style={{ alignItems: "flex-end" }}
              >
                {gridFilterAreaOpen ? (
                  <FilterAltOffOutlinedIcon />
                ) : (
                  <FilterAltOutlinedIcon />
                )}
              </IconButton>
            </Tooltip>

            {gridFilterAreaOpen && (
              <Tooltip title="Limpar Filtros" placement="top">
                <IconButton
                  onClick={handleClearFilters}
                  size="small"
                  color="primary"
                  style={{ alignItems: "flex-end" }}
                >
                  <CleaningServicesOutlined />
                </IconButton>
              </Tooltip>
            )}
          </div>
          <Tooltip title="Criar usuário" placement="top">
            <IconButton
              onClick={() => handleToUser(null)}
              size="small"
              color="primary"
              style={{ alignItems: "flex-end" }}
            >
              <AddIcon />
            </IconButton>
          </Tooltip>
        </FilterButtonGroup>

        {gridFilterAreaOpen && (
          <GridFilterArea container spacing={2}>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel id="select-filter">Filtrar por</InputLabel>
                <Select
                  label="Filtrar Por"
                  value={`${filterField}`}
                  onChange={e => {
                    setFilterField(`${e.target.value}`);
                  }}
                >
                  {fields.map(filter => {
                    return (
                      <MenuItem key={filter.field} value={filter.field}>
                        {filter.label}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Precisão</InputLabel>
                <Select
                  label="Precisão"
                  value={`${filterPrecision}`}
                  onChange={e => {
                    setFilterPrecision(`${e.target.value}`);
                  }}
                >
                  <MenuItem key={"equal"} value={"equal"}>
                    Igual
                  </MenuItem>
                  <MenuItem key={"containing"} value={"containing"}>
                    Contendo
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={8}>
              <TextField
                fullWidth
                label="Valor a filtrar"
                placeholder="Valor a filtrar"
                value={filterValue}
                onChange={handleTextFilterChange}
                size="small"
              />
            </Grid>
          </GridFilterArea>
        )}

        <TableContainer
          sx={{
            minHeight: "250px",
          }}
        >
          <Table
            sx={{
              padding: "16px",
            }}
          >
            <TableHead>
              <TableRow
                sx={{
                  "&:last-child td, &:last-child th": {
                    borderBottom: 1,
                    borderColor: "#E5E5E5",
                  },
                }}
              >
                <TableCell size="small" width={60}>
                  Ações
                </TableCell>

                <CustomTableSortLabel
                  perPage={perPage}
                  currentPage={currentPage}
                  orderField={orderField}
                  field="name"
                  label="Nome"
                  handleRequest={handlePagination}
                  orderDirection={orderDirection}
                  setField={setOrderField}
                  setDirection={setOrderDirection}
                />
                <CustomTableSortLabel
                  perPage={perPage}
                  currentPage={currentPage}
                  orderField={orderField}
                  field="username"
                  label="Usuário"
                  handleRequest={handlePagination}
                  orderDirection={orderDirection}
                  setField={setOrderField}
                  setDirection={setOrderDirection}
                />

                <CustomTableSortLabel
                  perPage={perPage}
                  currentPage={currentPage}
                  orderField={orderField}
                  field="email"
                  label="E-mail"
                  handleRequest={handlePagination}
                  orderDirection={orderDirection}
                  setField={setOrderField}
                  setDirection={setOrderDirection}
                />

                <CustomTableSortLabel
                  perPage={perPage}
                  currentPage={currentPage}
                  orderField={orderField}
                  field="typeDescription"
                  label="Tipo"
                  handleRequest={handlePagination}
                  orderDirection={orderDirection}
                  setField={setOrderField}
                  setDirection={setOrderDirection}
                />

                <CustomTableSortLabel
                  perPage={perPage}
                  currentPage={currentPage}
                  orderField={orderField}
                  field="isActive"
                  label="Ativo"
                  handleRequest={handlePagination}
                  orderDirection={orderDirection}
                  setField={setOrderField}
                  setDirection={setOrderDirection}
                />
              </TableRow>
            </TableHead>
            <TableBody>
              {users.length > 0 &&
                users.map(user => {
                  return (
                    <TableRow
                      key={user.id}
                      sx={{
                        "&:last-child td, &:last-child th": {
                          border: 0,
                        },
                      }}
                    >
                      <CustomMenuItem
                        id={user.id ? user.id : ""}
                        handleToEdit={handleToUser}
                        requestNumber={user.id}
                      />

                      <CustomTableCell
                        size="small"
                        title={user.name}
                        width={200}
                      >
                        {user.name}
                      </CustomTableCell>

                      <CustomTableCell
                        size="small"
                        title={user.username}
                        width={200}
                      >
                        {user.username}
                      </CustomTableCell>

                      <CustomTableCell
                        size="small"
                        title={user.email}
                        width={200}
                      >
                        {user.email}
                      </CustomTableCell>

                      <CustomTableCell size="small" width={100}>
                        {user.userType?.description}
                      </CustomTableCell>

                      <CustomTableCell size="small" width={100}>
                        {user.isActive ? "Sim" : "Não"}
                      </CustomTableCell>
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>

          {users.length === 0 && (
            <CustomNoContentTableBody description="Nenhum usuário encontrado" />
          )}
        </TableContainer>
        <CustomTablePagination
          totalRows={totalRows}
          currentPage={currentPage}
          perPage={perPage}
          handleRequest={handlePagination}
          setPerPage={setPerPage}
          setCurrentPage={setCurrentPage}
          orderField={orderField}
          orderDirection={orderDirection}
        />

        {loading && <BackdropCustom />}
      </PageCard>
    </>
  );
};
